import URI from "urijs";

import { VehicleType } from "@busbud/int-schemas/lib/vehicles";

import type { RoutePointOfInterest } from "@app/helpers/seo-entities";
import URIExpand from "@app/lib/uri-expand";
import { PAGES } from "@app/modules/results/constants/pages";
import type { Place } from "@app/modules/route/types";
import type { AccountCreationStatus } from "@app/types/checkout-types";
import type { Features } from "@app/types/experiments";
import type { SearchInfo, SearchLocationQuery } from "@app/types/search-types";

import type { SupportedLanguage, SupportedLocale } from "@config/locales";
import type { MaybeWhitelabel, PartnerConfig } from "@partners/partner.type";

import { buildUrl, getBusbudHomeUrl, getHomeUrl } from "./url-builder-helpers";
import {
  account_link_template,
  bus_tickets_page_link_template,
  checkout_link_template,
  country_link_template,
  deeplink_link_template,
  google_maps_link_template,
  home_link_template,
  image_link_template,
  operator_awards_template,
  purchase_unsuccessful_link_template,
  results_link_template,
  terms_link_template,
  thank_you_link_template,
  train_tickets_page_link_template
} from "../../../config/link-templates";
import { getSubLocaleByCountryCode } from "../../../lib/localization-helper";
import * as SearchHelper from "../../helpers/search-client";
import { computeQueryString } from "../../utils";
import { localeToLang } from "../../utils/localization";

export const operator_page_vehicle_types = [
  VehicleType.Carpool,
  VehicleType.Ferry,
  VehicleType.Train
] as const;

export type OperatorPageVehicleType =
  (typeof operator_page_vehicle_types)[number];

export interface UrlBuilderServiceOptions {
  locale: SupportedLocale;
  whitelabel?: MaybeWhitelabel;
  legacy_partner_settings?: PartnerConfig["whitelabel_settings"] | null;
  original_url?: string;
  redirect_url?: string;
  features?: Features;
  language?: SupportedLanguage;
}

export default class UrlBuilderService {
  public locale: SupportedLocale;
  private original_url?: string;
  private redirect_url?: string;
  private whitelabel?: MaybeWhitelabel;
  private legacy_partner_settings?: PartnerConfig["whitelabel_settings"] | null;
  private language?: SupportedLanguage;

  public constructor(options: UrlBuilderServiceOptions) {
    this.locale = options.locale;
    this.original_url = options.original_url;
    this.redirect_url = options.redirect_url;
    this.language = options.language;
    if (options.whitelabel && options.whitelabel.length) {
      this.whitelabel = options.whitelabel;
      this.legacy_partner_settings = options.legacy_partner_settings || {};
    }
  }

  public setOptions(options: {
    locale?: SupportedLocale;
    whitelabel?: MaybeWhitelabel;
    legacy_partner_settings?: PartnerConfig["whitelabel_settings"] | null;
  }) {
    this.locale = options.locale || this.locale;
    if (options.whitelabel && options.whitelabel.length) {
      this.whitelabel = options.whitelabel;
      this.legacy_partner_settings = options.legacy_partner_settings || {};
    }
  }

  public currentUrl(): string {
    return this.original_url || "";
  }

  public home(query?: { [k: string]: unknown } | undefined): string {
    return getHomeUrl({
      whitelabel: this.whitelabel,
      query,
      language: this.language,
      locale: this.locale
    });
  }

  public busbudHome(query?: { [k: string]: unknown } | undefined): string {
    return getBusbudHomeUrl({
      query,
      locale: this.locale
    });
  }

  public asyncCartWithCartCreated(search: SearchInfo, cart_id: string): string {
    return this.results(search, {
      cart_id,
      view: PAGES.ASYNC_CART
    });
  }

  public results(
    search: SearchInfo,
    query?: { [k: string]: unknown } | undefined,
    locale?: SupportedLocale
  ): string {
    const search_params = SearchHelper.getUrlParams(search);
    const { origin_location, destination_location, ...search_query } =
      SearchHelper.getUrlQuery(search);

    const uri = URIExpand(results_link_template[locale ?? this.locale], {
      origin_geohash: search_params.from,
      destination_geohash: search_params.to
    });
    uri.query(search_query);

    if (origin_location) {
      uri.addQuery(
        this.buildLocationsQueryArray(origin_location, "origin_location")
      );
    }

    if (destination_location) {
      uri.addQuery(
        this.buildLocationsQueryArray(
          destination_location,
          "destination_location"
        )
      );
    }

    const url = buildUrl(uri, query, this.whitelabel);

    return url;
  }

  private buildLocationsQueryArray(
    locations: SearchLocationQuery,
    array_name: string
  ): Record<string, boolean> {
    return Object.fromEntries(
      Object.entries(locations).map(([location_id, enabled]) => {
        const field_name = `${array_name}[${location_id}]`;

        return [field_name, enabled];
      })
    );
  }

  public deeplink(
    search: SearchInfo,
    departures: { outbound_id: string; return_id?: string },
    query?: { [k: string]: unknown } | undefined
  ): string {
    const search_params = SearchHelper.getUrlParams(search);
    const search_query = SearchHelper.getUrlQuery(search);

    const uri = URIExpand(deeplink_link_template[this.locale], {
      origin_geohash: search_params.from,
      destination_geohash: search_params.to,
      outbound_id: departures.outbound_id,
      return_id: departures.return_id
    });

    uri.query(search_query);
    uri.addQuery({
      referrer: this.whitelabel === "neredennereye" ? "neredennereye" : "busbud"
    });

    return buildUrl(uri, query, this.whitelabel);
  }

  public checkout(
    id: string,
    is_booking: boolean,
    query?: { [k: string]: unknown } | undefined
  ): string {
    const uri = URIExpand(checkout_link_template.index, {
      locale: this.locale
    });

    const query_obj = is_booking ? { booking_id: id } : { cart_id: id };

    uri.query(query_obj);

    this._maybeAddWhitelabelUtm(uri);

    return buildUrl(uri, query, this.whitelabel);
  }

  public checkoutReferral(
    referral_cart_id: string,
    query?: { [k: string]: unknown } | undefined
  ): string {
    const uri = URIExpand(checkout_link_template.referral, {
      locale: this.locale,
      referral_cart_id: referral_cart_id
    });

    this._maybeAddWhitelabelUtm(uri);

    return buildUrl(uri, query, this.whitelabel);
  }

  private _maybeAddWhitelabelUtm(uri: URI): void {
    if (
      !(
        this.whitelabel &&
        this.legacy_partner_settings &&
        this.legacy_partner_settings.busbud_checkout
      )
    ) {
      return;
    }

    const utm_tags = this.legacy_partner_settings.utm_checkout || {
      utm_source: undefined,
      utm_medium: undefined,
      utm_campaign: ""
    };
    uri.addQuery({
      whitelabel: false,
      utm_source: utm_tags.utm_source,
      utm_medium: utm_tags.utm_medium,
      utm_campaign: utm_tags.utm_campaign.replace("{locale}", this.locale)
    });
  }

  public thankYou(
    purchase_id: number,
    purchase_uuid: string,
    account_creation_status?: AccountCreationStatus | undefined,
    query?: { [k: string]: unknown } | undefined
  ): string {
    const uri = URIExpand(thank_you_link_template.index, {
      locale: this.locale,
      purchase_id: String(purchase_id)
    });

    uri.query({
      puuid: purchase_uuid
    });

    if (account_creation_status) {
      uri.addQuery({
        signup_status: account_creation_status
      });
    }

    return buildUrl(uri, query, this.whitelabel);
  }

  public purchaseUnsuccessful(
    purchase_id: number,
    purchase_uuid: string
  ): string {
    const uri = URIExpand(purchase_unsuccessful_link_template.index, {
      locale: this.locale,
      purchase_id: String(purchase_id)
    }).query({
      puuid: purchase_uuid
    });

    return buildUrl(uri);
  }

  public image(
    path: string,
    query?: { [k: string]: string | number } | undefined
  ): string {
    const uri = new URI(image_link_template);
    uri.segment(path);
    uri.query({
      auto: "compress,format"
    });

    if (query) {
      uri.addQuery(query);
    }

    return uri.href();
  }

  /**
   * @deprecated
   * @param locale
   */
  public busTicketsPage(locale: SupportedLocale = this.locale): string {
    return buildUrl(URI(bus_tickets_page_link_template[locale]));
  }

  public trainTicketsPage(locale: SupportedLocale = this.locale): string {
    return buildUrl(URI(train_tickets_page_link_template[locale]));
  }

  public accountForgotPassword(from_expired_link: boolean): string {
    const uri = URIExpand(account_link_template.forgot_password, {
      locale: this.locale
    });
    const query: Record<string, unknown> = {};

    if (from_expired_link) {
      query.expired = true;
    }

    return buildUrl(uri, query, this.whitelabel);
  }

  public accountSignup(
    params: {
      first_name?: string | undefined;
      last_name?: string | undefined;
      email?: string | undefined;
      cta_origin?: string | undefined;
      redirect_url?: string | undefined;
      carousel_start_idx?: number | undefined;
    } = {}
  ): string {
    const {
      first_name,
      last_name,
      email,
      cta_origin,
      redirect_url,
      carousel_start_idx
    } = params;
    const uri = URIExpand(account_link_template.signup, {
      locale: this.locale
    });

    const query: Record<string, unknown> = {};
    if (first_name) {
      query.first_name = first_name;
    }
    if (last_name) {
      query.last_name = last_name;
    }
    if (email) {
      query.email = email;
    }
    if (cta_origin) {
      query.cta_origin = cta_origin;
    }

    if (redirect_url) {
      query.redirect_url = redirect_url;
    }

    if (carousel_start_idx) {
      query.carousel_start_idx = carousel_start_idx;
    }

    return buildUrl(uri, query, this.whitelabel);
  }

  public accountSignin(
    params: {
      token?: string | undefined;
      email?: string | undefined;
      redirect_url?: string | undefined;
    } = {}
  ): string {
    const { token, email, redirect_url } = params;
    const uri = URIExpand(account_link_template.signin, {
      locale: this.locale
    });
    const query: Record<string, unknown> = {};
    if (token) {
      query.token = token;
    }
    if (email) {
      query.email = email;
    }
    if (redirect_url) {
      query.redirect_url = redirect_url;
    }

    return buildUrl(uri, query, this.whitelabel);
  }

  public accountLoyaltyProfile(): string {
    const uri = URIExpand(account_link_template.loyalty_profile, {
      locale: this.locale
    });

    return buildUrl(uri, undefined, this.whitelabel);
  }

  public accountSettings(status?: "success"): string {
    const uri = URIExpand(account_link_template.settings, {
      locale: this.locale
    });

    if (status) {
      if (status === "success") {
        uri.query({
          success: true
        });
      }
    }

    return buildUrl(uri, undefined, this.whitelabel);
  }

  public accountTrips(): string {
    const uri = URIExpand(account_link_template.trips, {
      locale: this.locale
    });

    return buildUrl(uri, undefined, this.whitelabel);
  }

  public accountPassengerProfiles(): string {
    const uri = URIExpand(account_link_template.passenger_profiles, {
      locale: this.locale
    });

    return buildUrl(uri, undefined, this.whitelabel);
  }

  public accountTrip(
    purchase_id: number,
    purchase_uuid?: string | undefined,
    query?: { [k: string]: unknown } | undefined
  ): string {
    const uri = URIExpand(account_link_template.trip, {
      locale: this.locale,
      purchase_id: String(purchase_id)
    });

    uri.query({
      puuid: purchase_uuid
    });

    return buildUrl(uri, query, this.whitelabel);
  }

  public accountLogout(redirect_to_current_page: boolean = true): string {
    const uri = URIExpand(account_link_template.logout, {
      locale: this.locale
    });

    const query: Record<string, unknown> = {};
    if (redirect_to_current_page && (this.original_url || this.redirect_url)) {
      query.redirect = this.redirect_url || this.original_url;
    }

    return buildUrl(uri, query, this.whitelabel);
  }

  public countryOrCountryHome(country_code2: string): string {
    const sub_locale = getSubLocaleByCountryCode(this.locale, country_code2);

    if (sub_locale) {
      const uri = URIExpand(home_link_template, {
        locale: sub_locale
      });

      return buildUrl(uri);
    }

    const uri = URIExpand(country_link_template, {
      locale: this.locale,
      country_code2: country_code2.toLowerCase()
    });

    return buildUrl(uri);
  }

  public partnerBanner(
    hero_image_url: string | null,
    default_image: string
  ): string {
    if (!hero_image_url) {
      hero_image_url = default_image;
    }

    return this._hero(hero_image_url, undefined);
  }

  public thumbnailHero(
    hero_image_url: string | null,
    is_archived: boolean = false
  ): string {
    if (!hero_image_url) {
      hero_image_url = "default-heroes/bus.jpg";
    }

    const is_absolute_url = hero_image_url.startsWith("http");
    if (!is_absolute_url) {
      const uri = new URI(image_link_template);
      uri.segment(hero_image_url);
      hero_image_url = uri.href();
    }
    const query_params = {
      auto: "compress,format",
      w: 256,
      h: 256,
      q: 50,
      fit: "crop",
      ...(is_archived && { duotone: "C420415A,D1EDFF" })
    };

    return URI(hero_image_url).query(query_params).toString();
  }

  public hero(
    hero_image_url: string | null,
    size?: { width: number; height: number }
  ): string {
    if (!hero_image_url) {
      hero_image_url = "default-heroes/bus.jpg";
    }

    return this._hero(hero_image_url, size);
  }

  private _hero(
    url: string,
    size: { width: number; height: number } | undefined
  ): string {
    const is_absolute_url = url.startsWith("http");
    if (!is_absolute_url) {
      const uri = new URI(image_link_template);
      uri.segment(url);
      url = uri.href();
    }

    const query_params = {
      auto: "compress,format",
      w: size?.width ?? 2048,
      h: size?.height ?? 500,
      q: 10
    };

    return URI(url).query(query_params).toString();
  }

  public withQuery(url: string, query?: qs.ParsedQs | undefined): string {
    return url + computeQueryString(query);
  }

  /**
   * @param {string} ticket_type
   * @param {number} departure_id
   * @param {number} purchase_id
   * @param {{[k: string]: any}|undefined} [query]
   * @returns {string}
   */
  public terms(
    ticket_type: string,
    departure_id: number,
    purchase_id: number,
    query?: { [k: string]: unknown } | undefined
  ): string {
    const uri = URIExpand(terms_link_template, {
      locale: this.locale,
      ticket_type,
      departure_id: String(departure_id)
    });
    uri.addQuery({
      purchase_id: purchase_id
    });

    return buildUrl(uri, query, this.whitelabel);
  }

  public googleMapsUrl(lat: number, lon: number): string {
    const uri = URIExpand(google_maps_link_template, {
      lat: String(lat),
      lon: String(lon)
    });

    return buildUrl(uri);
  }

  public saveToGooglePayButton(
    locale: SupportedLocale,
    query?: { [k: string]: unknown }
  ): string {
    const googleLocale = this.googleLocaleForLocale(locale);
    return this.image(
      `app-wallets/google/${encodeURIComponent(
        googleLocale
      )}_add_to_google_wallet_add-wallet-badge.svg`,
      {
        auto: "format",
        ...query
      }
    );
  }

  private googleLocaleForLocale(locale: string) {
    switch (locale) {
      case "de":
      case "de-de":
        return "de";
      case "en-ca":
        return "enCA";
      case "en-gb":
        return "enGB";
      case "en-us":
      case "en":
        return "enUS";
      case "tr":
      case "tr-tr":
        return "tr";
      case "es":
      case "es-es":
      case "es-mx":
        return "esES";
      case "es-419":
        return "es419";
      case "fr":
      case "fr-fr":
        return "frFR";
      case "fr-ca":
        return "frCA";
      case "it":
      case "it-it":
        return "it";
      case "nl":
      case "nl-nl":
        return "nl";
      case "pl":
      case "pl-pl":
        return "pl";
      case "pt":
      case "pt-pt":
      case "pt-br":
        return "pt";
      case "ru":
      case "ru-ru":
        return "ru";
      case "sv":
      case "sv-se":
        return "se";
      case "zh":
      case "zh-cn":
        return "zhHK";
      default:
        return "enUS";
    }
  }

  public trustPilotRating(): string {
    // Our matching lang locale
    // default to www.trustpilot.com
    //
    // br	https://br.trustpilot.com
    // ca	https://ca.trustpilot.com
    // fr	https://fr.trustpilot.com
    // de	https://de.trustpilot.com
    // it	https://it.trustpilot.com
    // nl	https://nl.trustpilot.com
    // pl	https://pl.trustpilot.com
    // pt	https://pt.trustpilot.com
    // es	https://es.trustpilot.com
    // uk	https://uk.trustpilot.com
    // us	https://www.trustpilot.com

    const subdomainMapping: Record<SupportedLocale, string> = {
      de: "de",
      "en-ca": "ca",
      "en-gb": "uk",
      es: "es",
      fr: "fr",
      "fr-ca": "ca",
      it: "it",
      nl: "nl",
      pl: "pl",
      pt: "br",
      "pt-pt": "pt",

      // Not supported by trustpilot for the moment
      "es-419": "www",
      "es-mx": "www",
      en: "www",
      ru: "www",
      sv: "www",
      tr: "www",
      zh: "www"
    };

    const countrySubdomain: string = subdomainMapping[this.locale] || "www";
    return `https://${countrySubdomain}.trustpilot.com/evaluate/www.busbud.com`;
  }

  public withQueryLocation(
    origin: Place,
    destination: Place
  ): Record<string, "true"> {
    const search_params: Record<string, "true"> = {};

    if (this.isPoi(origin) && origin.location_ids) {
      origin.location_ids.forEach(id => {
        search_params[`origin_location[${id}]`] = "true";
      });
    }

    if (this.isPoi(destination) && destination.location_ids) {
      destination.location_ids.forEach(id => {
        search_params[`destination_location[${id}]`] = "true";
      });
    }

    return search_params;
  }

  private isPoi(place: Place): place is RoutePointOfInterest {
    return "type" in place && place.type !== null;
  }

  public operatorAwards() {
    const uri = URIExpand(operator_awards_template, {
      language: localeToLang(this.locale)
    });

    return buildUrl(uri);
  }
}

export { UrlBuilderService };
